import React, { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CookieBeesWordmark } from '@/components/brand/cookiebees-wordmark';

// Public "choose a new password" page reached from the emailed reset link.
// endpoint differs for tenant vs superadmin; the token rides in the URL.
export default function ResetPassword({ endpoint = '/api/auth/reset-password', loginHref = '/' }) {
  const qs = new URLSearchParams(window.location.search);
  const token = qs.get('token') || '';
  // From a team invite: the same link, worded as a welcome.
  const welcome = qs.get('welcome') === '1';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.post(endpoint, { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset your password.');
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><CookieBeesWordmark className="h-8 w-auto text-foreground" /></div>
        <Card className="shadow-floating">
          <CardContent className="p-8">
            {!token ? (
              <p className="text-sm text-muted-foreground">
                This reset link is missing its token. Request a fresh one from the sign-in page.
              </p>
            ) : done ? (
              <div className="space-y-3 text-center">
                <CheckCircle2 className="mx-auto size-8 text-primary" />
                <h1 className="text-lg font-semibold">{welcome ? 'You are in' : 'Password updated'}</h1>
                <p className="text-sm text-muted-foreground">{welcome ? 'Sign in with your email and the password you just chose.' : 'You can now sign in with your new password.'}</p>
                <Button asChild className="w-full"><a href={loginHref}>Go to sign in</a></Button>
              </div>
            ) : (
              <>
                <h1 className="mb-1 text-xl font-semibold tracking-tight">{welcome ? 'Welcome to Cookiebees' : 'Choose a new password'}</h1>
                <p className="mb-6 text-sm text-muted-foreground">{welcome ? 'Set your password to sign in. At least 8 characters.' : 'At least 8 characters.'}</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="pw">{welcome ? 'Password' : 'New password'}</Label>
                    <Input id="pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required disabled={loading} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pw2">Confirm password</Label>
                    <Input id="pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required disabled={loading} />
                  </div>
                  {error && (
                    <div role="alert" className="rounded-md border border-destructive-soft bg-destructive-soft/60 px-3 py-2 text-sm text-destructive">{error}</div>
                  )}
                  <Button type="submit" disabled={loading || !pw || !pw2} className="w-full">
                    {loading ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : welcome ? 'Set password' : 'Update password'}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
